<template>
  <aside :aria-label="$t('general.post_media_files')" class="interactive-social-wall__modal-media-panel">
    <div class="interactive-social-wall__modal-media-panel-title">
      <span>
        <i aria-hidden="true" class="uil-images"/>
         {{$t('general.postal_media')}}
      </span>
      <small>{{ $t('general.file_count', { count: mediaFiles.length }) }}</small>
    </div>

    <div class="interactive-social-wall__modal-post-media"
         uk-slider
         uk-slider
         @beforeitemshow="pauseMediaVideos"
         @itemshow="pauseMediaVideos">
      <div class="interactive-social-wall__modal-media-viewport">
        <ul class="uk-slider-items interactive-social-wall__modal-media-slides">
          <li v-for="mediaItem in postMediaItems"
              :key="mediaItem.key"
              :title="mediaItem.displayName"
              class="interactive-social-wall__modal-media-slide">
            <span v-if="hasMediaLoadError(mediaItem.errorKey)"
                  class="interactive-social-wall__modal-media-fallback">
              <i :class="
                  mediaItem.isVideo ? 'uil-video-slash' : 'uil-image-broken'
                "
                 aria-hidden="true"
              />
              {{ $t('general.preview_unavailable') }}
            </span>
            <img v-else-if="mediaItem.isImage"
                 :alt="mediaItem.displayName"
                 :src="mediaItem.file.file_link"
                 loading="lazy"
                 @error="markMediaLoadError(mediaItem.errorKey)"/>
            <video v-else
                   :aria-label="mediaItem.displayName"
                   :src="mediaItem.file.file_link"
                   controls
                   playsinline
                   preload="metadata"
                   @error="markMediaLoadError(mediaItem.errorKey)"/>
            <small>
              <i :class="mediaItem.isVideo ? 'uil-video' : 'uil-image'" aria-hidden="true"/>
              {{ mediaItem.isVideo ? $t('general.video') : $t('general.image') }}
            </small>
          </li>
        </ul>
      </div>

      <template v-if="mediaFiles.length > 1">
        <a :aria-label="$t('general.previous_media')"
           class="interactive-social-wall__modal-media-nav interactive-social-wall__modal-media-nav--prev"
           href="#"
           uk-slider-item="previous"
           @click.prevent="pauseMediaVideos">
          <i aria-hidden="true" class="uil-angle-left"/>
        </a>
        <a :aria-label="$t('general.next_media')"
           class="interactive-social-wall__modal-media-nav interactive-social-wall__modal-media-nav--next"
           href="#"
           uk-slider-item="next"
           @click.prevent="pauseMediaVideos">
          <i aria-hidden="true" class="uil-angle-right"/>
        </a>
      </template>
    </div>
  </aside>
</template>

<script>
import { withRecordValue } from "../utils/state";

export default {
  name: "SocialWallPostMedia",
  props: {
    mediaFiles: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      failedMediaByKey: {},
    };
  },
  computed: {
    postMediaItems() {
      return this.mediaFiles.map((file, index) => {
        const fileIdentity = file?.id ?? index;
        const fileType = Number(file?.file_type);

        return {
          key: `interactive-social-wall-media-${fileIdentity}`,
          errorKey: `${fileIdentity}-${file?.file_link ?? "media"}`,
          file,
          displayName:
              file?.name || file?.file_name || `Multimedya ${index + 1}`,
          isImage: fileType === 1,
          isVideo: fileType === 2,
        };
      });
    },
  },
  watch: {
    mediaFiles() {
      this.failedMediaByKey = {};
    },
  },
  methods: {
    hasMediaLoadError(errorKey) {
      return Boolean(this.failedMediaByKey[errorKey]);
    },
    markMediaLoadError(errorKey) {
      this.failedMediaByKey = withRecordValue(
          this.failedMediaByKey,
          errorKey,
          true
      );
    },
    pauseMediaVideos() {
      Array.from(this.$el?.querySelectorAll("video") || []).forEach((video) => {
        if (!video.paused) video.pause();
      });
    },
  },
};
</script>

<style scoped>
.interactive-social-wall__modal-media-panel,
.interactive-social-wall__modal-media-panel * {
  box-sizing: border-box;
}

.interactive-social-wall__modal-media-panel {
  min-width: 0;
  padding: 14px;
  background: #f3f6f8;
  border-right: 1px solid #e0e5eb;
}

.interactive-social-wall__modal-media-panel-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 9px;
  color: #5f6b7c;
}

.interactive-social-wall__modal-media-panel-title > span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 8.5px;
  font-weight: 700;
  line-height: 1.3;
  text-transform: uppercase;
}

.interactive-social-wall__modal-media-panel-title > span i {
  font-size: 13px;
}

.interactive-social-wall__modal-media-panel-title > small {
  flex: 0 0 auto;
  color: #8993a2;
  font-size: 7.5px;
}

.interactive-social-wall__modal-post-media {
  position: relative;
  width: 100%;
}

.interactive-social-wall__modal-media-viewport {
  width: 100%;
  overflow: hidden;
  background: #e9edf2;
  border: 1px solid #dce3ea;
  border-radius: 9px;
}

.interactive-social-wall__modal-media-slides {
  height: clamp(210px, 25vw, 310px);
  margin: 0;
  padding: 0;
  list-style: none;
}

.interactive-social-wall__modal-media-slide {
  position: relative;
  flex: 0 0 auto;
  width: 100% !important;
  height: 100%;
  margin: 0;
  overflow: hidden;
  background: #e9edf2;
}

.interactive-social-wall__modal-media-slide img,
.interactive-social-wall__modal-media-slide video {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.interactive-social-wall__modal-media-slide video {
  background: #111827;
}

.interactive-social-wall__modal-media-slide small {
  --iconColor: #ffffff;
  position: absolute;
  right: 4px;
  bottom: 4px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 3px 5px;
  color: #ffffff;
  font-size: 7px;
  font-weight: 600;
  line-height: 1;
  pointer-events: none;
  background: rgba(17, 24, 39, 0.72);
  border-radius: 4px;
}

.interactive-social-wall__modal-media-slide small i {
  font-size: 9px;
}

.interactive-social-wall__modal-media-nav {
  --iconColor: #ffffff;
  position: absolute;
  top: 50%;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  color: #ffffff !important;
  text-decoration: none !important;
  background: rgba(17, 24, 39, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.32);
  border-radius: 50%;
  box-shadow: 0 3px 10px rgba(15, 23, 42, 0.2);
  transform: translateY(-50%);
  transition: background-color 150ms ease, transform 150ms ease;
}

.interactive-social-wall__modal-media-nav:hover,
.interactive-social-wall__modal-media-nav:focus-visible {
  color: #ffffff !important;
  background: rgba(17, 24, 39, 0.9);
  transform: translateY(-50%) scale(1.04);
}

.interactive-social-wall__modal-media-nav i {
  font-size: 22px;
  line-height: 1;
}

.interactive-social-wall__modal-media-nav--prev {
  left: 9px;
}

.interactive-social-wall__modal-media-nav--next {
  right: 9px;
}

.interactive-social-wall__modal-media-fallback {
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  color: #7d8898;
  font-size: 7.5px;
  text-align: center;
}

.interactive-social-wall__modal-media-fallback i {
  font-size: 18px;
}

@media (min-width: 761px) {
  .interactive-social-wall__modal-media-panel {
    display: flex;
    height: 100%;
    min-height: 0;
    flex-direction: column;
  }

  .interactive-social-wall__modal-post-media {
    display: flex;
    flex: 1 1 auto;
    min-height: 0;
    flex-direction: column;
  }

  .interactive-social-wall__modal-media-viewport {
    flex: 1 1 auto;
    min-height: 0;
    height: 100%;
  }

  .interactive-social-wall__modal-media-slides {
    height: 100%;
    min-height: 0;
  }
}

@media (max-width: 760px) {
  .interactive-social-wall__modal-media-panel {
    padding: 14px 18px;
    border-right: 0;
    border-bottom: 1px solid #e5e9ef;
  }

  .interactive-social-wall__modal-media-slides {
    height: clamp(190px, 56vw, 290px);
  }
}

@media (max-width: 480px) {
  .interactive-social-wall__modal-media-panel {
    padding-right: 14px;
    padding-left: 14px;
  }
}
</style>
